import { Text as TextDS } from '@cash-me/react-components'
import styled, { css } from 'styled-components'

import { Infos as InfosDS, Section as SectionDS } from 'components/general-styles/General'
import { theme } from 'styles/theme'

import type { HelpLayoutVariant } from './help.model'

type SectionProps = {
  $buttonAlign?: 'center' | 'left'
  $layoutVariant: HelpLayoutVariant
  $longFormMinHeight: number | string
}

type InfoProps = {
  $previousBG: string
  $layoutVariant: HelpLayoutVariant
  $longFormMinHeight: number | string
}

type AccordionWrapperProps = {
  $bg: 'default' | 'medium' | 'contrast' | 'contrast-medium' | undefined
  $layoutVariant: HelpLayoutVariant
}

const { colors, media, space, borderWidths, fontSizes, lineHeights, radii } = theme

const getLongFormMinHeight = (minHeight: number | string) =>
  typeof minHeight === 'number' ? `${minHeight}px` : minHeight

const setColor = color => {
  if (color.match(/^[A-F\d]{6}$|^[A-F\d]{3}$|^[A-F\d]{8}$|^[A-F\d]{4}$/i) !== null) {
    return `#${color}`
  } else {
    return colors[color]
  }
}

export const Section = styled(SectionDS)<SectionProps>`
  .ds-button {
    display: flex;
    justify-content: center;
    border-radius: ${radii['corner-radius-pill']};
    width: 100%;
  }

  @media (${media.phone}) {
    padding-top: 0;
  }

  @media (${media.tablet}) {
    .ds-button {
      background: ${colors['neutral-background-contrast']};
      max-width: 240px;
      margin: ${props => (props.$buttonAlign === 'center' ? '0 auto' : '0')};
    }
  }

  ${({ $bg, $layoutVariant, $longFormMinHeight }) =>
    $layoutVariant === 'long-form' &&
    css`
      @media (${media.tablet}) {
        padding-top: 0;
        padding-bottom: 0;

        ${$bg !== 'contrast' && `background-color: #f4f6fb;`}

        .ds-button {
          gap: ${space['spacing-sm']};
          width: 230px;
          max-width: none;
          height: 77px;
          min-height: 77px;
          margin: 81px 0 0;
        }
      }

      @media (${media.desktop}) {
        > div {
          min-height: ${getLongFormMinHeight($longFormMinHeight)};
        }
      }
    `}
`

export const Infos = styled(InfosDS)<InfoProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 250px;

  .polygon-pink {
    display: none;
  }

  h2,
  button {
    position: relative;
    z-index: 2;
  }

  h2 {
    padding: ${space['spacing-md']} 0 0;
  }

  /* Blue background */
  &::before {
    content: ' ';
    background-color: ${colors['blue-background-strong-default']};
    display: block;
    position: absolute;
    top: 0;
    left: -${space['spacing-md']};
    right: -${space['spacing-md']};
    height: 100%;
    border-top-right-radius: 50px;
    border-top-left-radius: 50px;
    z-index: 1;
  }

  /* White background in S.Info */
  &::after {
    content: ' ';
    background-color: ${props => setColor(props.$previousBG)};
    display: block;
    position: absolute;
    top: -${space['spacing-xxxl']};
    left: -${space['spacing-md']};
    right: -${space['spacing-md']};
    height: 100%;
    z-index: 0;
  }

  @media (${media.tablet}) {
    justify-content: space-between;
    padding: 8rem 0 ${space['spacing-giga']};
    padding-right: ${space['spacing-giga']};
    height: calc(550px + 13rem);

    h2 {
      padding: 0;
    }

    .polygon-pink {
      display: block;
      position: absolute;
      top: 8rem;
      right: 0;
      z-index: 1;
    }

    &::before {
      z-index: 0;
      left: unset;
      top: 0;
      right: 0;
      width: 50vw;
      height: 100%;
      border-top-right-radius: 150px;
      border-bottom-right-radius: 150px;
      border-top-left-radius: unset;
      transform: translateX(-${space['spacing-xxl']});
    }

    &::after {
      display: none;
    }
  }

  @media (${media.desktop}) {
    .polygon-pink {
      transform: translateX(calc(50% + ${space['spacing-lg']}));
    }

    &::before {
      transform: translateX(${space['spacing-lg']});
    }
  }

  ${({ $layoutVariant, $longFormMinHeight }) =>
    $layoutVariant === 'long-form' &&
    css`
      @media (${media.tablet}) {
        box-sizing: border-box;
        height: 100%;
        justify-content: flex-start;
        padding-top: 72px;
        padding-bottom: 0;

        h2,
        h2 span {
          max-width: 383px;
          margin-bottom: 0;
          font-size: ${fontSizes['font-size-heading-2']};
          line-height: ${lineHeights['font-line-height-sm']};
          letter-spacing: -0.96px;
        }

        &::before {
          border-bottom-right-radius: 0;
        }
      }

      @media (${media.desktop}) {
        min-height: ${getLongFormMinHeight($longFormMinHeight)};
        padding-right: ${space['spacing-xl']};

        .polygon-pink {
          top: 188px;
          transform: translateX(calc(50% + ${space['spacing-xl']}));
        }

        &::before {
          min-height: ${getLongFormMinHeight($longFormMinHeight)};
          transform: translateX(${space['spacing-xl']});
        }
      }
    `}
`

export const AccordionWrapper = styled.div<AccordionWrapperProps>`
  margin-bottom: ${space['spacing-xl']};

  .ds-icon {
    color: ${props => (props['$bg'] === 'contrast' ? colors['cyan-background-pressed'] : colors['blue-icon-default'])};
  }

  .ds-accordion-item {
    margin-bottom: ${space['spacing-xxs']};
    padding-bottom: ${space['spacing-xxs']};
    border-bottom: ${borderWidths['border-stroke-micro']} solid ${colors['neutral-border-default']};

    a {
      cursor: pointer;
    }

    div > a {
      margin-bottom: ${space['spacing-sm']};
    }
  }

  @media (${media.desktop}) {
    margin-bottom: ${space['spacing-xxl']};
  }

  ${({ $layoutVariant }) =>
    $layoutVariant === 'long-form' &&
    css`
      @media (${media.tablet}) {
        padding-top: 72px;
        padding-bottom: ${space['spacing-ultra']};
        margin-bottom: 0;
      }

      @media (${media.desktop}) {
        width: 100%;
        max-width: 531px;
        padding-top: 72px;
        padding-bottom: ${space['spacing-ultra']};
        margin-bottom: 0;

        .ds-accordion-item {
          min-height: 80px;
          padding-bottom: 0;
          margin-bottom: 0;

          > summary {
            gap: ${space['spacing-xl']};
            align-items: center;
            min-height: 79px;
            padding: 0;

            > div {
              flex: 1;
            }
          }

          h3 {
            line-height: ${lineHeights['font-line-height-lg']};
          }

          .ds-icon {
            width: 28px;
            height: 28px;
            flex-shrink: 0;
          }
        }
      }
    `}
`

export const NodeContent = styled.div`
  > * {
    margin-bottom: ${space['spacing-sm']};
  }

  > *:last-child {
    margin-bottom: ${space['spacing-md']};
  }
`

export const ChecklistItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${space['spacing-md']};
  color: ${colors['neutral-text-weak']};
  line-height: ${lineHeights['font-line-height-lg']};

  span.checklist-icon {
    position: relative;
    top: -${space['spacing-nano']};
    margin-right: ${space['spacing-xxxs']};
  }
`

export const Text = styled(TextDS)`
  margin-bottom: ${space['spacing-sm']};
`
